import { useRef } from 'react';
import { Button, BUTTON_THEMES, CalloutBox, CalloutBoxThemes } from 'ht-styleguide';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import cn from 'classnames';

/* Components */
import Maybe from '@components/Maybe';

/* Hooks */
import useOutsideClick from '@hooks/useOutsideClick';
import { useRouter } from 'next/router';

/* Constants & Utils */
import { formatPriceTypeHourly, formatter } from '@utils/currency';
import { UPSELL_PLAN_BILLING_CYCLES } from '@constants/plan';

/* Styles */
import styles from './cartnavigationdisplay.module.scss';

/* Types */
import { TPlanDisplayProps, TBreakdownDisplayProps, TCartNavigationProps } from './CartNavigationDisplay.types';
import { useCartNavigationDisplay } from './CartNavigationDisplay.hooks';
import { MODAL_TYPES } from './CartNavigationDisplay.RemoveItemModal';
import { isDevelopment } from 'src/utils/environment';

const linkStyles = 'text-link p2 underline text-weight-medium';
const skuAndPlanNameStyles = 'p1 text-weight-medium';

const RemoveItemModalDynamic = dynamic(() => import('./CartNavigationDisplay.RemoveItemModal'), { ssr: false });
const PlanDisplay = ({ plan, toggleremovePlanModal }: TPlanDisplayProps) => {
  if (!plan || !plan.id) return null;

  /* Constants */
  const { name, planInterval, amount } = plan;
  const interval = planInterval === UPSELL_PLAN_BILLING_CYCLES.ANNUAL ? 'year' : 'month';

  return (
    <div className={styles.cardDetails}>
      <div className={styles.leftSide}>
        <div className="marginRight-small">
          <img alt={name} src="/_nextassets_/icons/shp-cart-thumbnail.webp" className={styles.image} />
        </div>
        <div>
          <p className={skuAndPlanNameStyles}>{name}</p>
          <button type="button" className="plainButton">
            <span className={linkStyles} onClick={toggleremovePlanModal}>
              Remove
            </span>
          </button>
        </div>
      </div>
      <div className={styles.rightSide}>
        <p className="p1 n900">{`${formatter(0).format(amount)}/${interval}`}</p>
        <p className="caption n700 text-align-right">Due today</p>
      </div>
    </div>
  );
};

/**
 * Breakdown Display for Single Item
 * @param {BreakdownItem} breakdown
 * @returns {JSX.Element | null}
 * @constructor
 */
const BreakdownDisplay = ({ breakdownItems, getEditSkuLink, toggleRemoveServiceModal }: TBreakdownDisplayProps) => {
  if ((breakdownItems || []).length === 0) return null;

  const [service] = breakdownItems;
  const { image, name, subTotal } = service;

  const subTotalAmount = formatPriceTypeHourly({
    sku: service.priceType,
    price: subTotal.amount || '0',
    withAsterik: true,
  });

  const hasMembershipDiscount = Boolean(subTotal.lineThroughText);

  const subTotalStyles = cn({
    blue700: hasMembershipDiscount,
    [`text-weight-medium`]: hasMembershipDiscount,
    n900: !hasMembershipDiscount,
  });

  const src = isDevelopment() ? 'https://picsum.photos/id/237/56/56' : image;

  return (
    <div className={styles.cardDetails}>
      <div className={styles.leftSide}>
        {src ? (
          <Image src={src} alt={name} width={48} height={48} quality={100} className={cn(styles.image, 'marginRight-small')} />
        ) : (
          <div className={cn(styles.image, 'marginRight-small')} />
        )}
        <div>
          <div className={skuAndPlanNameStyles}>{name}</div>
          <Link href={getEditSkuLink(String(service.skuId))} className={cn(linkStyles, styles.editText)}>
            Edit details
          </Link>

          <button type="button" className="plainButton" onClick={toggleRemoveServiceModal}>
            <span className={linkStyles}>Remove</span>
          </button>
        </div>
      </div>
      <div className={styles.rightSide}>
        <p className={cn(styles.amount, 'p1 text-align-right')}>
          {subTotal.lineThroughText && <span className="line-through n700">{subTotal.lineThroughText}</span>}
          <span className={subTotalStyles}>&nbsp;{subTotalAmount}</span>
        </p>
        <p className="caption n700 text-align-right">Due after service</p>
      </div>
    </div>
  );
};

const Spacer = () => <div className={styles.spacer} />;

const CartNavigationDisplay = ({ toggleCartDisplay, offsetRightSmall, offsetRightMedium = true }: TCartNavigationProps) => {
  /* Hooks */
  const router = useRouter();
  const wrapperRef = useRef(null);
  const {
    breakdownItems,
    cartedPlan,
    getEditSkuLink,
    isInBookingFlow,
    removePlan,
    removeSku,
    showRemovePlanModal,
    showRemoveServiceModal,
    startBookingProcess,
    toggleRemovePlanModal,
    toggleRemoveServiceModal,
  } = useCartNavigationDisplay();

  /* Any clicks outside, close this display */
  useOutsideClick(wrapperRef, toggleCartDisplay);

  // Use medium offset by default
  const offsetStyles = cn({
    [styles.offsetRightSmall]: offsetRightSmall,
    [styles.offsetRightMedium]: offsetRightMedium && !offsetRightSmall,
  });

  const showBookingButton = !isInBookingFlow(router.pathname);

  const isPriceTypeHourly = (breakdownItems[0] || {}).priceType === 'hourly';

  const hasServiceInCart = (breakdownItems || []).length > 0;

  return (
    <div ref={wrapperRef} className={cn(styles.cartDisplayContainer, offsetStyles)}>
      <BreakdownDisplay breakdownItems={breakdownItems} getEditSkuLink={getEditSkuLink} toggleRemoveServiceModal={toggleRemoveServiceModal} />
      {hasServiceInCart && <Spacer />}
      <PlanDisplay plan={cartedPlan} toggleremovePlanModal={toggleRemovePlanModal} />
      <Spacer />
      <CalloutBox theme={CalloutBoxThemes.PRIMARY} text="Have a promo code? You'll have the chance to apply it near the end of this booking." />

      {showBookingButton && (
        <>
          <Spacer />
          <Button theme={BUTTON_THEMES.PRIMARY} onClick={startBookingProcess}>
            Book Now
          </Button>
        </>
      )}

      {isPriceTypeHourly && (
        <>
          <Spacer />
          <p className="p2 n700">
            *Hourly services are charged for a minimum of one hour, and billable in half-hour increments thereafter. Though we'll try our very best,
            resolution is not always possible and can't be guaranteed.
          </p>
        </>
      )}
      <Maybe condition={showRemoveServiceModal}>
        <RemoveItemModalDynamic
          isVisible={showRemoveServiceModal}
          hide={toggleRemoveServiceModal}
          onConfirm={() => removeSku(router.pathname)}
          type={MODAL_TYPES.SERVICE}
        />
      </Maybe>
      <Maybe condition={showRemovePlanModal}>
        <RemoveItemModalDynamic
          isVisible={showRemovePlanModal}
          hide={toggleRemovePlanModal}
          onConfirm={() => removePlan(router.pathname)}
          type={MODAL_TYPES.MEMBERSHIP}
        />
      </Maybe>
    </div>
  );
};

export default CartNavigationDisplay;
